import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type TeamMember = {
  user_id: string;
  shelter_id: string;
  role: "owner" | "member";
  granted_at: string;
  email?: string | null;
};

export type TeamInvite = {
  id: string;
  shelter_id: string;
  invited_email: string;
  status: "pending" | "accepted" | "rejected" | "expired" | "cancelled";
  created_at: string;
  expires_at: string;
};

/** Czy zalogowany user jest ownerem (a nie tylko member) któregoś schroniska. */
export const useIsShelterOwnerRole = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["is-shelter-owner-role", userId],
    enabled: !!userId,
    queryFn: async (): Promise<{ shelter_id: string } | null> => {
      const { data, error } = await supabase
        .from("shelter_owners")
        .select("shelter_id, role")
        .eq("user_id", userId!)
        .eq("role", "owner" as any)
        .maybeSingle();
      if (error) throw error;
      return data ? { shelter_id: data.shelter_id } : null;
    },
  });
};

/** Lista członków zespołu dla schroniska (widoczne tylko dla własnego schroniska/admina przez RLS). */
export const useShelterTeam = (shelterId: string | undefined) => {
  return useQuery({
    queryKey: ["shelter-team", shelterId],
    enabled: !!shelterId,
    queryFn: async (): Promise<TeamMember[]> => {
      const { data, error } = await (supabase as any).rpc("get_shelter_team_members", {
        _shelter_id: shelterId!,
      });
      if (error) throw error;
      return (data ?? []) as TeamMember[];
    },
  });
};

/** Pending zaproszenia dla schroniska (widoczne tylko dla ownera przez RLS). */
export const useShelterInvites = (shelterId: string | undefined) => {
  return useQuery({
    queryKey: ["shelter-invites", shelterId],
    enabled: !!shelterId,
    queryFn: async (): Promise<TeamInvite[]> => {
      const { data, error } = await (supabase as any)
        .from("shelter_team_invites")
        .select("id, shelter_id, invited_email, status, created_at, expires_at")
        .eq("shelter_id", shelterId!)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as TeamInvite[];
    },
  });
};

export const useInviteTeamMember = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ shelterId, email }: { shelterId: string; email: string }) => {
      const { data, error } = await supabase.functions.invoke("invite-team-member", {
        body: { shelter_id: shelterId, email },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["shelter-invites", vars.shelterId] });
    },
  });
};

export const useCancelInvite = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ inviteId, shelterId }: { inviteId: string; shelterId: string }) => {
      const { error } = await (supabase as any)
        .from("shelter_team_invites")
        .update({ status: "cancelled" })
        .eq("id", inviteId);
      if (error) throw error;
      return { shelterId };
    },
    onSuccess: (r) => qc.invalidateQueries({ queryKey: ["shelter-invites", r.shelterId] }),
  });
};

export const useRemoveTeamMember = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, shelterId }: { userId: string; shelterId: string }) => {
      const { error } = await supabase
        .from("shelter_owners")
        .delete()
        .eq("user_id", userId)
        .eq("shelter_id", shelterId);
      if (error) throw error;
      return { shelterId };
    },
    onSuccess: (r) => qc.invalidateQueries({ queryKey: ["shelter-team", r.shelterId] }),
  });
};

export const useAcceptTeamInvite = () => {
  return useMutation({
    mutationFn: async (token: string) => {
      const { data, error } = await supabase.functions.invoke("accept-team-invite", {
        body: { token },
      });
      if (error) throw error;
      if ((data as any)?.error) throw new Error((data as any).error);
      return data as { success: boolean; shelter?: { id: string; name: string; slug: string } };
    },
  });
};
